import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

// Platform configurations with distinct visual identity
const platformConfig: Record<string, {
  icon: string;
  gradient: string;
  textColor: string;
  platformBadgeColor: string;
  platformBadgeBg: string;
  linkText: string;
  displayName: string;
}> = {
  tiktok: {
    icon: '▶',
    gradient: 'linear-gradient(135deg, #010101 0%, #161616 100%)',
    textColor: '#ffffff',
    platformBadgeColor: '#ffffff',
    platformBadgeBg: 'rgba(254, 44, 85, 0.9)',
    linkText: 'Watch',
    displayName: 'TikTok',
  },
  instagram: {
    icon: '◎',
    gradient: 'linear-gradient(135deg, #833AB4 0%, #C13584 40%, #E1306C 70%, #F56040 100%)',
    textColor: '#ffffff',
    platformBadgeColor: '#ffffff',
    platformBadgeBg: 'rgba(0, 0, 0, 0.35)',
    linkText: 'View',
    displayName: 'Instagram',
  },
  linkedin: {
    icon: 'in',
    gradient: 'linear-gradient(135deg, #0A66C2 0%, #004182 100%)',
    textColor: '#ffffff',
    platformBadgeColor: '#ffffff',
    platformBadgeBg: 'rgba(0, 0, 0, 0.25)',
    linkText: 'View',
    displayName: 'LinkedIn',
  },
  facebook: {
    icon: 'f',
    gradient: 'linear-gradient(135deg, #1877F2 0%, #0D5DC7 100%)',
    textColor: '#ffffff',
    platformBadgeColor: '#ffffff',
    platformBadgeBg: 'rgba(0, 0, 0, 0.2)',
    linkText: 'View',
    displayName: 'Facebook',
  },
  twitter: {
    icon: '𝕏',
    gradient: 'linear-gradient(135deg, #000000 0%, #14171A 100%)',
    textColor: '#ffffff',
    platformBadgeColor: '#ffffff',
    platformBadgeBg: 'rgba(29, 161, 242, 0.9)',
    linkText: 'View',
    displayName: 'X',
  },
  x: {
    icon: '𝕏',
    gradient: 'linear-gradient(135deg, #000000 0%, #14171A 100%)',
    textColor: '#ffffff',
    platformBadgeColor: '#000000',
    platformBadgeBg: 'rgba(255, 255, 255, 0.9)',
    linkText: 'View',
    displayName: 'X',
  },
  youtube: {
    icon: '▶',
    gradient: 'linear-gradient(135deg, #FF0000 0%, #CC0000 100%)',
    textColor: '#ffffff',
    platformBadgeColor: '#FF0000',
    platformBadgeBg: 'rgba(255, 255, 255, 0.95)',
    linkText: 'Watch',
    displayName: 'YouTube',
  },
};

const defaultConfig = {
  icon: '📱',
  gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  textColor: '#ffffff',
  platformBadgeColor: '#ffffff',
  platformBadgeBg: 'rgba(0, 0, 0, 0.3)',
  linkText: 'View',
  displayName: 'Social',
};

interface PostCardProps {
  brand: string;
  platform: string;
  message: string;
  engagementScore?: number | string;
  date?: string;
  url?: string;
}

const formatEngagement = (score: number | string): string => {
  const num = typeof score === 'string' ? parseFloat(score.replace(/,/g, '')) : score;
  if (isNaN(num)) return String(score);
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toLocaleString();
};

const PostCard: React.FC<PostCardProps> = ({
  brand,
  platform,
  message,
  engagementScore,
  date,
  url,
}) => {
  const platformKey = platform.toLowerCase().replace(/\s+/g, '');
  const config = platformConfig[platformKey] || defaultConfig;

  // Truncate message if too long
  const truncatedMessage = message.length > 150 ? message.slice(0, 147) + '...' : message;

  return (
    <Box
      sx={{
        background: config.gradient,
        borderRadius: '10px',
        padding: '12px 14px',
        marginBottom: '8px',
        maxWidth: '100%',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      }}
    >
      {/* Platform Badge - Top Left, Prominent */}
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          background: config.platformBadgeBg,
          borderRadius: '4px',
          padding: '3px 8px',
          marginBottom: '8px',
        }}
      >
        <Box
          component="span"
          sx={{
            fontSize: '11px',
            fontWeight: 700,
            color: config.platformBadgeColor,
            lineHeight: 1,
          }}
        >
          {config.icon}
        </Box>
        <Typography
          sx={{
            fontSize: '0.7rem',
            fontWeight: 700,
            color: config.platformBadgeColor,
            letterSpacing: '0.02em',
            lineHeight: 1,
          }}
        >
          {config.displayName}
        </Typography>
      </Box>

      {/* Header: Brand + Date + Engagement */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: '0.85rem',
              fontWeight: 600,
              color: config.textColor,
              lineHeight: 1.3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {brand}
          </Typography>
          {date && (
            <Typography
              sx={{
                fontSize: '0.7rem',
                color: 'rgba(255,255,255,0.7)',
                marginTop: '1px',
              }}
            >
              {date}
            </Typography>
          )}
        </Box>
        {engagementScore && (
          <Box
            sx={{
              background: 'rgba(0, 0, 0, 0.35)',
              borderRadius: '14px',
              padding: '4px 10px',
              fontSize: '0.72rem',
              fontWeight: 600,
              color: '#ffffff',
              whiteSpace: 'nowrap',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              backdropFilter: 'blur(4px)',
            }}
          >
            <Box
              component="span"
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: '#ff6b35',
                display: 'inline-block',
              }}
            />
            {formatEngagement(engagementScore)}
          </Box>
        )}
      </Box>

      {/* Message */}
      <Typography
        sx={{
          fontSize: '0.8rem',
          color: 'rgba(255,255,255,0.92)',
          lineHeight: 1.45,
          mb: url ? 1 : 0,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {truncatedMessage}
      </Typography>

      {/* Link button */}
      {url && (
        <Box
          component="a"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            fontSize: '0.72rem',
            fontWeight: 600,
            color: 'rgba(255,255,255,0.85)',
            textDecoration: 'none',
            padding: '3px 0',
            transition: 'color 0.15s ease',
            '&:hover': {
              color: '#ffffff',
              textDecoration: 'underline',
            },
          }}
        >
          {config.linkText} <OpenInNewIcon sx={{ fontSize: 13 }} />
        </Box>
      )}
    </Box>
  );
};

export default PostCard;
